import { useEffect, useRef, useState } from 'react';
import { getApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import {
  getDatabase,
  onDisconnect,
  onValue,
  ref,
  remove,
  serverTimestamp,
  set,
  update,
} from 'firebase/database';
import '@/firebase'; // initialises the default Firebase app

const DATABASE_URL =
  'https://kaizenflow-3790d-default-rtdb.europe-west1.firebasedatabase.app';

// Presence records outlive a session by this much, so a paused session isn't
// dropped. onDisconnect removes records when a tab closes; endsAt is the
// backstop that hides any record onDisconnect missed.
const GRACE_MS = 10 * 60 * 1000;
const HEARTBEAT_MS = 4 * 60 * 1000;

export interface Peer {
  uid: string;
  startedAt: number;
  endsAt: number;
  seat: number;
  // Focused time excluding breaks: focusedMs as of focusedAt (server time),
  // growing live from then while running
  focusedMs?: number;
  focusedAt?: number;
  running?: boolean;
  tz?: string; // browser timezone, only if they share where they are
}

/** Whole minutes someone has focused, breaks excluded, at server time `serverNow`. */
export const focusedMinutes = (p: Pick<Peer, 'focusedMs' | 'focusedAt' | 'running'>, serverNow: number) => {
  const live = p.running && p.focusedAt ? Math.max(0, serverNow - p.focusedAt) : 0;
  return Math.floor(((p.focusedMs ?? 0) + live) / 60000);
};

/** "47 min", "1 h 05 min" */
export const formatFocus = (minutes: number) =>
  minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;

const MILESTONES: Record<number, string> = {
  30: '30 minutes',
  60: 'an hour',
  90: 'an hour and a half',
  120: 'two hours',
};

/** The milestone crossed going from `before` to `after` minutes, if any. */
export const crossedMilestone = (before: number, after: number) => {
  const hit = Object.keys(MILESTONES)
    .map(Number)
    .filter((m) => before < m && after >= m);
  return hit.length ? hit[hit.length - 1] : null;
};

/** e.g. "someone in Seoul by the window just passed an hour", "you've focused for 30 minutes" */
export const describeMilestone = (
  minutes: number,
  who: { self: true } | { label?: string; city?: string | null }
) =>
  'self' in who
    ? `you've focused for ${MILESTONES[minutes]}`
    : `someone ${who.city ? `in ${who.city} ` : ''}${who.label ? `${who.label} ` : ''}just passed ${MILESTONES[minutes]}`;

let uidPromise: Promise<string> | null = null;

// Reuses a signed-in user (e.g. Google login for rooms), otherwise signs in
// anonymously so each browser can only write its own presence record.
const getUid = () => {
  uidPromise ??= new Promise<string>((resolve, reject) => {
    const auth = getAuth(getApp());
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        if (user) resolve(user.uid);
        else signInAnonymously(auth).then((cred) => resolve(cred.user.uid), reject);
      },
      reject
    );
  }).catch((e) => {
    uidPromise = null;
    throw e;
  });
  return uidPromise;
};

const database = () => getDatabase(getApp(), DATABASE_URL);

/** Lowest pool seat nobody else has claimed; seatCount means "no seat free". */
export const firstFreeSeat = (peers: Peer[], seatCount: number) => {
  const taken = new Set(peers.map((p) => p.seat));
  for (let i = 0; i < seatCount; i++) if (!taken.has(i)) return i;
  return seatCount;
};

/**
 * Places each peer in a seat for display. Peers keep the seat they claimed;
 * if two claimed the same one, the earlier arrival keeps it and the later one
 * moves to the next free seat. Peers beyond the seat count are overflow.
 */
export const arrangeSeats = (peers: Peer[], seatCount: number) => {
  const seated = new Map<number, Peer>();
  const overflow: Peer[] = [];
  const pending: Peer[] = [];

  for (const p of [...peers].sort((a, b) => a.startedAt - b.startedAt)) {
    if (p.seat < seatCount && !seated.has(p.seat)) seated.set(p.seat, p);
    else pending.push(p);
  }
  for (const p of pending) {
    let i = 0;
    while (i < seatCount && seated.has(i)) i++;
    if (i < seatCount) seated.set(i, p);
    else overflow.push(p);
  }
  return { seated, overflow };
};

/** uid -> seat index for everyone visible in the scene (-1 = overflow, not drawn) */
export const seatsByUid = (peers: Peer[], seatCount: number) => {
  const { seated, overflow } = arrangeSeats(peers, seatCount);
  const map = new Map<string, number>();
  seated.forEach((p, seat) => map.set(p.uid, seat));
  overflow.forEach((p) => map.set(p.uid, -1));
  return map;
};

export interface SeatingChange {
  joined: number[]; // seat indexes (-1 = overflow)
  left: number[];
}

export const diffSeating = (
  before: Map<string, number>,
  after: Map<string, number>
): SeatingChange => ({
  joined: [...after].filter(([uid]) => !before.has(uid)).map(([, seat]) => seat),
  left: [...before].filter(([uid]) => !after.has(uid)).map(([, seat]) => seat),
});

/**
 * One quiet line describing a change, e.g. "someone in Seoul sat down by the
 * window". cityOf names where a newly seated person is, when they share it.
 */
export const describeChange = (
  change: SeatingChange,
  labelOf: (seat: number) => string | undefined,
  cityOf: (seat: number) => string | null | undefined = () => null
): string | null => {
  const phrase = (verb: string, seats: number[], place: string, withCity: boolean) => {
    if (seats.length > 1) return `${seats.length} people ${verb} ${place}`.trim();
    const label = labelOf(seats[0]);
    const city = withCity ? cityOf(seats[0]) : null;
    return `someone ${city ? `in ${city} ` : ''}${verb} ${label ?? place}`.trim();
  };
  if (change.joined.length) return phrase('sat down', change.joined, 'in the café', true);
  if (change.left.length) return phrase('got up', change.left, 'and left', false);
  return null;
};

interface PresenceOptions {
  /** Announce this browser (true while in a session) */
  active: boolean;
  /** Seconds left in the session, used to set when the record expires */
  remaining: number;
  seatCount: number;
  /** Seconds focused so far this session (the timer only runs while focused) */
  focusedSeconds: number;
  /** False while on a break */
  running: boolean;
  /** Timezone to share, or undefined to keep where you are private */
  tz?: string;
}

/** Everyone else focusing in `worldId`, plus this browser while `active`. */
export function usePresence(
  worldId: string,
  { active, remaining, seatCount, focusedSeconds, running, tz }: PresenceOptions
) {
  const [peers, setPeers] = useState<Peer[]>([]);
  const [uid, setUid] = useState<string | null>(null);
  const [serverOffset, setServerOffset] = useState(0);
  const peersRef = useRef<Peer[]>([]);
  const remainingRef = useRef(remaining);
  remainingRef.current = remaining;
  const focusRef = useRef({ focusedSeconds, running });
  focusRef.current = { focusedSeconds, running };
  const tzRef = useRef(tz);
  tzRef.current = tz;

  // Local clock correction, so everyone's focus times line up
  useEffect(
    () => onValue(ref(database(), '.info/serverTimeOffset'), (snap) => setServerOffset(snap.val() ?? 0)),
    []
  );

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    getUid()
      .then((id) => {
        if (cancelled) return;
        setUid(id);
        unsubscribe = onValue(
          ref(database(), `presence/${worldId}`),
          (snap) => {
            const now = Date.now();
            const all = (snap.val() ?? {}) as Record<string, Omit<Peer, 'uid'>>;
            const others = Object.entries(all)
              .map(([key, v]) => ({ uid: key, ...v }))
              .filter((p) => p.uid !== id && p.endsAt > now);
            peersRef.current = others;
            setPeers(others);
          },
          (err) => console.warn('[presence] read failed', err)
        );
      })
      .catch((err) => console.warn('[presence] sign-in failed', err));

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [worldId]);

  useEffect(() => {
    if (!active || !uid) return;

    const mine = ref(database(), `presence/${worldId}/${uid}`);
    const expiry = () => Date.now() + remainingRef.current * 1000 + GRACE_MS;
    const focus = () => ({
      focusedMs: focusRef.current.focusedSeconds * 1000,
      focusedAt: serverTimestamp(),
      running: focusRef.current.running,
    });

    onDisconnect(mine)
      .remove()
      .then(() =>
        set(mine, {
          startedAt: serverTimestamp(),
          endsAt: expiry(),
          seat: firstFreeSeat(peersRef.current, seatCount),
          ...focus(),
          ...(tzRef.current ? { tz: tzRef.current } : {}),
        })
      )
      .catch((err) => console.warn('[presence] announce failed', err));

    const heartbeat = setInterval(() => {
      update(mine, { endsAt: expiry(), ...focus() }).catch(() => {});
    }, HEARTBEAT_MS);

    return () => {
      clearInterval(heartbeat);
      remove(mine).catch(() => {});
      onDisconnect(mine).cancel().catch(() => {});
    };
  }, [active, uid, worldId, seatCount]);

  // Breaks pause the focus clock for everyone watching
  useEffect(() => {
    if (!active || !uid) return;
    update(ref(database(), `presence/${worldId}/${uid}`), {
      focusedMs: focusRef.current.focusedSeconds * 1000,
      focusedAt: serverTimestamp(),
      running,
    }).catch(() => {});
  }, [running, active, uid, worldId]);

  // Sharing where you are can be switched on or off mid-session
  useEffect(() => {
    if (!active || !uid) return;
    update(ref(database(), `presence/${worldId}/${uid}`), { tz: tz ?? null }).catch(() => {});
  }, [tz, active, uid, worldId]);

  return { peers, uid, serverOffset };
}
